import { type RefObject } from "react";
import { categoryLabel, sourceLabel } from "../lib/labels";
import type { Facets, Shelf, SortMode } from "../lib/types";

const SHELVES: { key: Shelf; label: string; title?: string }[] = [
  { key: "playable", label: "Playable" },
  {
    key: "multiplayer",
    label: "Multiplayer",
    title: "Games you play head-to-head against others, with a reArcade account",
  },
  { key: "flash", label: "Flash" },
  { key: "java", label: "Java" },
  { key: "lab", label: "Held", title: "Applets awaiting missing assets or a runtime fix" },
];

interface Props {
  searchText: string;
  onSearch: (v: string) => void;
  searchRef: RefObject<HTMLInputElement>;
  shelf: Shelf;
  onShelf: (s: Shelf) => void;
  category: string;
  onCategory: (v: string) => void;
  source: string;
  onSource: (v: string) => void;
  sort: SortMode;
  onSort: (s: SortMode) => void;
  facets?: Facets;
}

// The 36px filter toolbar — search + category/source/sort selects + runtime
// shelves. Markup mirrors the original index.html so the copied CSS applies 1:1.
export function CatalogToolbar(props: Props) {
  const { facets } = props;
  return (
    <div className="controls">
      <div className="toolbar">
        <label className="seek">
          <input
            ref={props.searchRef}
            type="search"
            placeholder="Find a game"
            aria-label="Find a game"
            autoComplete="off"
            value={props.searchText}
            onChange={(e) => props.onSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") props.onSearch("");
            }}
          />
          <kbd className="seek-key" aria-hidden="true">
            /
          </kbd>
        </label>

        <select
          className={`pick${props.category ? " picked" : ""}`}
          aria-label="Category"
          value={props.category}
          onChange={(e) => props.onCategory(e.target.value)}
        >
          <option value="">All categories</option>
          {facets?.categories.map((c) => (
            <option key={c.value} value={c.value}>
              {categoryLabel(c.value)} · {c.count}
            </option>
          ))}
        </select>

        <select
          className={`pick${props.source ? " picked" : ""}`}
          aria-label="Source website"
          value={props.source}
          onChange={(e) => props.onSource(e.target.value)}
        >
          <option value="">All sources</option>
          {facets?.sources.map((s) => (
            <option key={s.value} value={s.value}>
              {sourceLabel(s.value)} · {s.count}
            </option>
          ))}
        </select>

        <select
          className={`pick${props.sort !== "az" ? " picked" : ""}`}
          aria-label="Sort order"
          value={props.sort}
          onChange={(e) => props.onSort(e.target.value as SortMode)}
        >
          <option value="az">Sort: A–Z</option>
          <option value="plays">Sort: most played</option>
        </select>

        <div className="shelves" role="group" aria-label="Runtime filter">
          {SHELVES.map((s) => (
            <button
              key={s.key}
              type="button"
              className={props.shelf === s.key ? "active" : undefined}
              aria-pressed={props.shelf === s.key}
              title={s.title}
              onClick={() => props.onShelf(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
